<template>
  <div class="w-full bg-[#191919] h-[53px] z-[10030]">
    <div
      class="w-full px-3 md:px-5 py-[10px] flex items-center justify-between sticky mx-auto"
      :class="sidebarOpen ? 'max-w-[1920px]' : 'max-w-full'"
    >
      <div class="flex gap-4">
        <!--
          AB#21246 — one static icon gave no clue that this was a control, let
          alone what it would do. Two icons that name the outcome: chevrons
          pointing the way the menu is about to move, in a target with a hover
          state so it reads as a button before it is clicked.
        -->
        <button
          type="button"
          class="hdr-toggle"
          :title="sidebarOpen ? 'Collapse menu' : 'Expand menu'"
          :aria-label="sidebarOpen ? 'Collapse menu' : 'Expand menu'"
          :aria-expanded="sidebarOpen"
          aria-controls="app-sidebar"
          @click="toggleSidebar"
        >
          <ChevronDoubleLeftIcon v-if="sidebarOpen" class="size-5 text-white" />
          <ChevronDoubleRightIcon v-else class="size-5 text-white" />
        </button>
        <!--
          AB#21246 — the glyph beside the title was a fixed chart icon, so it
          said "dashboard" on every screen in the admin, the same way the words
          did. It now comes from the same navigation entry the title does, which
          is the icon the user clicked in the menu to get here.
        -->
        <component
          :is="lucideIcons[pageIcon] ?? lucideIcons['LayoutGrid']"
          class="size-6 text-white hidden md:inline-block shrink-0"
          aria-hidden="true"
        />
        <!-- AB#21246 — was the literal string "Dashboard" on every screen. -->
        <h1 class="heading text-white text-xl mb-0 font-semibold truncate">{{ pageTitle }}</h1>
      </div>
      <!--
        AB#21246 — a 40%-wide search field sat here, `disabled` and `readonly`,
        with no v-model and no handler. It has never searched anything. A
        control that looks like a control and does nothing is worse than no
        control: people type into it and conclude the search is broken.

        Removed rather than wired up, because making it work is a feature with
        its own questions — what it searches, and where the results go — not a
        fix. Raised separately.
      -->
      <div class="flex items-center gap-3">
        <!-- AB#21594 — Needs Attention entry point. A pill that names the count
             instead of a bare icon, so the number of orders needing the person
             is legible at a glance and the control reads as a live signal, not
             decoration. Turns a calm translucent state when the queue is clear
             into a red-ringed alert when it is not. Only for roles that hold
             view-needs-attention; the queue itself is scoped server-side. -->
        <!-- AB#21836 — Messages bell: what this person has been sent (receive-desk-messages). -->
        <MessagesBell v-if="canReceiveDeskMessages" />
        <!-- AB#21966 — Order chats: unread team chat messages (view-order-team-chat). -->
        <TeamChatBell v-if="canSeeTeamChat" />
        <!-- AB#21807 — Comms mood badge (view-own-communication-mood; hidden while off). -->
        <CommsMoodBadge v-if="!isPhone" />
        <!--
          AB#22137 — on a phone the queue pills (one to three of them) and the
          mood badge do not fit beside the title. They fold behind one button:
          the shield with the delayed count, which opens a sheet listing each
          queue with its numbers. Same counts, same destinations.
        -->
        <Dropdown
          v-if="isPhone && (badges.length || canSeeOwnMood)"
          trigger-style="hdr-queues"
          trigger-aria-label="Needs Attention queues"
          :close-on-outside-click="true"
          data-test="hdr-queues"
        >
          <template #trigger>
            <span class="hdr-queues-in" :class="`hdr-queues-in--${phoneTone}`">
              <ShieldExclamationIcon v-if="phoneTotal > 0" class="na-entry-ic" />
              <ShieldCheckIcon v-else class="na-entry-ic" />
              <span v-if="phoneDelayed > 0" class="na-entry-count" data-test="hdr-queues-delayed">{{
                cap(phoneDelayed)
              }}</span>
              <span v-else-if="phoneTotal > 0" class="na-entry-total">{{ cap(phoneTotal) }}</span>
            </span>
          </template>
          <template #contents>
            <div class="hdr-sheet" data-test="hdr-queues-sheet">
              <h3 class="hdr-sheet-title">Needs Attention</h3>
              <button
                v-for="b in badges"
                :key="b.key"
                type="button"
                class="hdr-sheet-row"
                :data-test="`hdr-queue-${b.key}`"
                @click="goToNeedsAttention(b.key)"
              >
                <BanknotesIcon v-if="b.key === 'finance'" class="hdr-sheet-ic" />
                <Headset v-else-if="isCsm(b.key)" class="hdr-sheet-ic" />
                <ShieldExclamationIcon v-else class="hdr-sheet-ic" />
                <span class="hdr-sheet-name">{{ phoneName(b.key) }}</span>
                <span v-if="b.count === 0" class="hdr-sheet-clear">Clear</span>
                <template v-else>
                  <span v-if="b.levels.delayed > 0" class="hdr-sheet-delayed"
                    >{{ cap(b.levels.delayed) }} delayed</span
                  >
                  <span class="hdr-sheet-total">{{ b.count > 999 ? '999+' : b.count }}</span>
                </template>
              </button>
              <div v-if="canSeeOwnMood" class="hdr-sheet-mood">
                <span class="hdr-sheet-name">Your comms mood</span>
                <CommsMoodBadge />
              </div>
            </div>
          </template>
        </Dropdown>
        <!-- AB#21999 — one pill per queue the person holds. Someone with both
             (a super admin) gets the order admin shield and a finance pill
             beside it, so finance issues are never hidden behind the other
             queue's number, and an order in both queues is not counted twice. -->
        <button
          v-for="b in isPhone ? [] : badges"
          :key="b.key"
          class="na-entry"
          :class="[
            b.count === 0
              ? 'na-entry--calm'
              : b.levels.delayed > 0 || !b.levelsKnown
                ? 'na-entry--alert'
                : 'na-entry--open',
            { 'na-entry--pair': badges.length > 1 },
          ]"
          :data-test="
            b.key === 'finance' && badges.length > 1
              ? 'na-entry-finance'
              : b.key === 'csm' && badges.length > 1
                ? 'na-entry-csm'
                : b.key === 'csm_eu' && badges.length > 1
                  ? 'na-entry-csm-eu'
                  : 'na-entry'
          "
          :title="b.title"
          :aria-label="b.label"
          @click="goToNeedsAttention(b.key)"
        >
          <BanknotesIcon v-if="b.key === 'finance' && badges.length > 1" class="na-entry-ic" />
          <!-- AB#22134 — the CSM pill: a headset, Customer Success, so it is
               never confused with the Team chat bubble or the order shield. -->
          <Headset v-else-if="isCsm(b.key)" class="na-entry-ic" data-test="na-entry-csm-ic" />
          <!-- AB#22175 / AB#22183 — each CS pill says its desk: NA or EU. -->
          <span v-if="b.key === 'csm'" class="na-entry-tag" data-test="na-entry-csm-na-tag"
            >NA</span
          >
          <span v-if="b.key === 'csm_eu'" class="na-entry-tag" data-test="na-entry-csm-eu-tag"
            >EU</span
          >
          <ShieldExclamationIcon v-else-if="b.count > 0" class="na-entry-ic" />
          <ShieldCheckIcon v-else class="na-entry-ic" />
          <!-- AB#21985 — the red number is what is delayed; the plain one is the
               whole queue (all three levels), so the total does not read as alarms. -->
          <template v-if="b.count > 0 && b.levelsKnown">
            <span v-if="b.levels.delayed > 0" class="na-entry-count" data-test="na-entry-delayed">{{
              cap(b.levels.delayed)
            }}</span>
            <span class="na-entry-total" data-test="na-entry-total">{{
              b.count > 999 ? '999+' : b.count
            }}</span>
          </template>
          <!-- A backend that does not send levels yet: one number, as before. -->
          <span v-else-if="b.count > 0" class="na-entry-count">{{ cap(b.count) }}</span>
          <span v-else class="na-entry-label">Clear</span>
        </button>
        <!-- <button
          class="focus:outline-none focus-visible:outline-none size-10 rounded-full bg-[#FFFFFF0D] justify-center items-center notification-button relative hidden md:flex"
        >
          <BellIcon class="size-6 text-white inline-block" />
          <span class="size-3 text-[#DF0404] absolute top-[10px] right-[10px]"></span>
        </button> -->
        <button
          class="hdr-account focus:outline-none focus-visible:outline-none cursor-pointer flex items-center gap-1.5 duration-100 relative dropdown-icon"
          @click="toggleDropdown"
          @blur="toggleDropdown"
          ref="dopdownToggler"
          aria-haspopup="true"
          :aria-expanded="dropdownOpen"
          :aria-label="user?.name ? `Account menu — ${user.name}` : 'Account menu'"
          :title="user?.name || undefined"
        >
          <UserAvatar
            :name="user?.name"
            :initials="userInitials"
            :src="user?.avatar_url"
            :size="38"
            class="hdr-avatar"
            data-test="header-avatar"
          />
          <ChevronDownIcon class="size-4 text-white/70 hidden md:inline-block" />

          <div class="dropdown-menu" v-if="dropdownOpen">
            <!-- The name lives here now; the bar keeps the avatar only. -->
            <div
              class="dropdown-header flex items-center gap-2 justify-center"
              data-test="header-user-name"
            >
              <UserAvatar
                :name="user?.name"
                :initials="userInitials"
                :src="user?.avatar_url"
                :size="22"
              />
              {{ user?.name }}
            </div>
            <ul class="dropdown-list">
              <li
                class="dropdown-item dropdown-item--toggle px-5 py-3 mb-0 text-[15px] font-medium whitespace-nowrap text-left flex items-center justify-between gap-3"
                @click.stop="toggleTheme"
              >
                <span class="flex items-center gap-2">
                  <MoonIcon v-if="isDark" class="size-5 text-[#009FE3] inline-block" />
                  <SunIcon v-else class="size-5 text-[#009FE3] inline-block" />
                  Dark mode
                </span>
                <span
                  class="hdr-switch"
                  :class="{ on: isDark }"
                  role="switch"
                  :aria-checked="isDark"
                >
                  <span class="hdr-switch-dot"></span>
                </span>
              </li>
              <!-- AB#22138 — phone notifications on this device (receive-push-notifications). -->
              <li
                v-if="push.offered.value"
                class="dropdown-item dropdown-item--toggle px-5 py-3 mb-0 text-[15px] font-medium whitespace-nowrap text-left flex items-center justify-between gap-3"
                data-test="header-push"
                @click="openPushSettings"
              >
                <span class="flex items-center gap-2">
                  <BellAlertIcon class="size-5 text-[#009FE3] inline-block" />
                  Notifications
                </span>
                <span v-if="push.state.value === 'needs-install'" class="hdr-push-hint"
                  >Home Screen</span
                >
                <span v-else-if="push.state.value === 'blocked'" class="hdr-push-hint"
                  >Blocked</span
                >
                <span
                  v-else
                  class="hdr-switch"
                  :class="{ on: push.state.value === 'on' }"
                  role="switch"
                  :aria-checked="push.state.value === 'on'"
                  :aria-busy="push.busy.value"
                  data-test="header-push-switch"
                  @click.stop="togglePush"
                >
                  <span class="hdr-switch-dot"></span>
                </span>
              </li>
              <li
                class="dropdown-item px-5 py-3 mb-0 text-[15px] font-medium whitespace-nowrap text-left"
                @click="
                  openQuickAccess({
                    name: 'General Settings',
                    to: '/general-settings',
                  })
                "
              >
                <Cog8ToothIcon class="size-5 text-[#009FE3] inline-block mr-2" />
                Settings
              </li>
              <li
                class="dropdown-item px-5 py-3 mb-0 text-[15px] font-medium whitespace-nowrap text-left"
                @click="store.logout"
              >
                <ArrowLeftStartOnRectangleIcon class="size-5 text-[#009FE3] inline-block mr-2" />
                Logout
              </li>
            </ul>
          </div>
        </button>
      </div>
    </div>
    <CommsMoodDrawer v-if="canSeeCommsMood" />
    <!-- AB#21836 — the one place a desk message takes the screen. -->
    <DeskMessageHost v-if="canReceiveDeskMessages" />
  </div>
</template>
<script setup lang="ts">
import { openV2Application } from '@/composables';
import { isV2OwnedPath, ownedPathsForUser } from '@/composables/useMenuHref';
import type { NavigationMenuItem } from '@/models';
import { useMainStore } from '@/store';
import UserAvatar from '@/components/Shared/UserAvatar.vue';
import { rememberSidebarState } from '@/utils/tablePreferences';
import {
  ArrowLeftStartOnRectangleIcon,
  BanknotesIcon,
  BellAlertIcon,
  ChevronDoubleLeftIcon,
  ChevronDoubleRightIcon,
  Cog8ToothIcon,
  ChevronDownIcon,
  MoonIcon,
  ShieldCheckIcon,
  ShieldExclamationIcon,
  SunIcon,
} from '@heroicons/vue/24/solid';
import { onClickOutside } from '@vueuse/core';
import { pageIconFor, pageTitleFor } from '@/utils/pageTitle';
import * as lucideIcons from 'lucide-vue-next';
import { Headset } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { can } from '@/composables';
import { useNeedsAttention } from '@/composables/useNeedsAttention';
import { useTheme } from '@/composables/useTheme';
import CommsMoodBadge from '@/components/CommsMood/CommsMoodBadge.vue';
import DeskMessageHost from '@/components/DeskMessages/DeskMessageHost.vue';
import MessagesBell from '@/components/DeskMessages/MessagesBell.vue';
import TeamChatBell from '@/components/TeamChat/TeamChatBell.vue';
import Dropdown from '@/components/Shared/Dropdown.vue';
import { usePhone } from '@/composables/usePhone';
import { usePushNotifications } from '@/composables/usePushNotifications';
import { showToast } from '@/composables/swal';
import { deskMessagesReceive } from '@/composables/useDeskMessages';
import CommsMoodDrawer from '@/components/CommsMood/CommsMoodDrawer.vue';
import { OWN_PERMISSION, TEAM_PERMISSION } from '@/composables/useCommsMood';

const router = useRouter();
const route = useRoute();

// AB#21595 — the light/dark toggle in Quick Access.
const { isDark, toggle: toggleTheme } = useTheme();

/**
 * Quick Access has to route by the same ownership list the sidebar uses. It used to
 * hand every entry to the legacy app, so Settings opened admin.custimoo.com even
 * though this app has served /general-settings since the Settings pilot.
 */
const openQuickAccess = (item: NavigationMenuItem): void => {
  const { role, permissions } = useMainStore();
  const owned = ownedPathsForUser(permissions);
  if (isV2OwnedPath(item.to, role?.name ?? null, owned)) router.push(item.to);
  else openV2Application(item);
};

const { user, sidebarOpen, navigation } = storeToRefs(useMainStore());

/**
 * AB#21597 — the account control is now the person's initials, not the company
 * logo (which was the same image for everyone and read as an avatar that never
 * changed). Initials are the first letters of the first two words of the name,
 * uppercased; a one-word name gives one letter, an empty name a neutral dot.
 */
const userInitials = computed(() => {
  const name = (user.value?.name ?? '').trim();
  if (name === '') return '·';
  const parts = name.split(/\s+/).filter(Boolean);
  const letters =
    (parts[0]?.[0] ?? '') + (parts.length > 1 ? (parts[parts.length - 1][0] ?? '') : '');
  return letters.toUpperCase() || '·';
});

/**
 * AB#21246 — the heading names the page you are on.
 *
 * It was the literal string "Dashboard", so Users, Factories, Price List and
 * every other screen announced itself as the dashboard. The words come from the
 * sidebar, which is what the user clicked to get here, so the header agrees
 * with the menu rather than inventing a second vocabulary for the same screens.
 */
const pageTitle = computed(() =>
  pageTitleFor(route.path, route.name ? String(route.name) : null, navigation.value)
);

/** The same menu entry's icon, so the glyph and the words cannot disagree. */
const pageIcon = computed(() => pageIconFor(route.path, navigation.value));
const store = useMainStore();
const dropdownOpen = ref<boolean>(false);

/*
 * AB#22138 — the "Phone notifications" row. On: permission, this device's
 * subscription, and a test so the person sees one arrive. Off: this device is
 * removed. On an iPhone outside the Home Screen app, or with notifications
 * blocked in the phone's settings, the row says what to do instead.
 */
const push = usePushNotifications();
const togglePush = async (): Promise<void> => {
  if (push.busy.value) return;
  if (push.state.value === 'needs-install') {
    showToast(
      'On an iPhone, add the portal to your Home Screen first (Share, then Add to Home Screen), open it from there, and turn notifications on.',
      'info'
    );
    return;
  }
  if (push.state.value === 'blocked') {
    showToast(
      "Notifications are blocked for the portal in this phone's or browser's settings. Allow them there, then try again.",
      'warning'
    );
    return;
  }
  if (push.state.value === 'on') {
    await push.disable();
    showToast('Notifications are off on this device.', 'info');
    return;
  }
  if (await push.enable()) {
    showToast('Notifications are on. A test is on its way.', 'success');
    void push.test();
  } else if ((push.state.value as string) === 'blocked') {
    showToast(
      "Notifications were not allowed. You can allow them in the phone's settings.",
      'warning'
    );
  } else {
    showToast('Notifications could not be turned on on this device.', 'error');
  }
};
/* AB#22138 — the row itself opens the Notifications sheet: this device, and
   which kinds of notification reach the person. The switch stays a shortcut. */
const openPushSettings = (): void => {
  dropdownOpen.value = false;
  push.openSettings();
};
const dopdownToggler = ref<HTMLElement | null>(null);
const toggleDropdown = (): void => {
  dropdownOpen.value = !dropdownOpen.value;
};
const toggleSidebar = (): void => {
  sidebarOpen.value = !sidebarOpen.value;
  // AB#21240 — this is the only place a human states a preference about the
  // menu; everywhere else in the app writes `sidebarOpen` as a side effect of
  // something else. Remembering it here is what lets the dashboard default to
  // collapsed without overruling someone who has said they want it open.
  rememberSidebarState(globalThis.localStorage, sidebarOpen.value);
};
onClickOutside(dopdownToggler, () => {
  dropdownOpen.value = false;
});

/**
 * AB#21587 — the Needs Attention badge.
 *
 * Only present for roles that hold the permission; the button navigates to the
 * queue, and the count refreshes on mount and on a slow poll so the shield
 * reflects what is waiting without the person opening the page. The poll is
 * deliberately gentle — this is a nudge, not a live feed — and it stops when the
 * header unmounts so it never leaks a timer.
 */
const canSeeNeedsAttention = computed(
  () =>
    can('view-needs-attention') ||
    can('view-finance-needs-attention') ||
    // AB#22133 — the CSM queue (and AB#22134 the CSM team) has a pill of its own.
    can('view-csm-needs-attention') ||
    can('view-csm-team-needs-attention') ||
    // AB#22175 — and the CSM EU desk, with a pill of its own.
    can('view-csm-eu-needs-attention') ||
    can('view-csm-eu-team-needs-attention')
);
const canSeeTeamChat = computed(() => can('view-order-team-chat'));
// AB#21807 — the one Comms mood drawer; the badge, the Needs Attention chip and
// the team grid all open it.
const canSeeCommsMood = computed(
  () =>
    can(OWN_PERMISSION) ||
    can(TEAM_PERMISSION) ||
    // AB#22134 — a Lead CS Manager opens CSM team members' drawers.
    can('view-csm-team-communication-mood')
);
// AB#21836 — desk messages reach only people the backend flagged as receivers.
const canReceiveDeskMessages = computed(() => deskMessagesReceive(store.features));
const ownAttention = useNeedsAttention();
const financeAttention = useNeedsAttention('finance');
const csmAttention = useNeedsAttention('csm');
const isSuperAdmin = computed(() =>
  ((store.user as { roles?: { name?: string }[] } | undefined)?.roles ?? []).some(
    (r) => (r?.name ?? '').toLowerCase() === 'superadmin'
  )
);
// AB#22134 — the CSM team's number, for a lead (or a super admin) who holds
// the CSM team overview but looks after no account of their own: their CSM
// pill shows the whole team, as the order admin and finance pills do.
const csmTeamAttention = useNeedsAttention('csm-team');
const csmMode = computed<'me' | 'team' | null>(() => {
  // AB#22183 — a Super Admin's CSM queue is the whole desk, so it is "me" too.
  if (
    can('view-csm-needs-attention') &&
    ((csmAttention.accounts.value ?? 0) > 0 || isSuperAdmin.value)
  )
    return 'me';
  if (can('view-csm-team-needs-attention')) return 'team';
  return null;
});
// AB#22175 — the CSM EU pill: the same rule on the CSM EU desk.
const csmEuAttention = useNeedsAttention('csm-eu');
const csmEuTeamAttention = useNeedsAttention('csm-eu-team');
const csmEuMode = computed<'me' | 'team' | null>(() => {
  if (
    can('view-csm-eu-needs-attention') &&
    ((csmEuAttention.accounts.value ?? 0) > 0 || isSuperAdmin.value)
  )
    return 'me';
  if (can('view-csm-eu-team-needs-attention')) return 'team';
  return null;
});
const isCsm = (key: BadgeKey): boolean => key === 'csm' || key === 'csm_eu';
const csmModeOf = (key: BadgeKey) => (key === 'csm_eu' ? csmEuMode.value : csmMode.value);
const csmName = (key: BadgeKey): string => (key === 'csm_eu' ? 'CSM EU' : 'CSM NA');
const sourceOf = (key: BadgeKey) =>
  key === 'finance'
    ? financeAttention
    : key === 'csm'
      ? csmMode.value === 'team'
        ? csmTeamAttention
        : csmAttention
      : key === 'csm_eu'
        ? csmEuMode.value === 'team'
          ? csmEuTeamAttention
          : csmEuAttention
        : ownAttention;
const cap = (n: number): string => (n > 99 ? '99+' : String(n));

/**
 * AB#21999 — one badge per queue the person holds. An order admin gets theirs,
 * a Financial Controller gets the finance queue's, and someone holding both (a
 * super admin) gets both side by side. The two are never added together: an
 * order with a finance issue and an order admin issue sits in both queues.
 */
type BadgeKey = 'own' | 'finance' | 'csm' | 'csm_eu';
const badgeKeys = computed<BadgeKey[]>(() => {
  const keys: BadgeKey[] = [];
  if (can('view-needs-attention')) keys.push('own');
  if (can('view-finance-needs-attention')) keys.push('finance');
  // AB#22134 — a CSM pill for someone who looks after at least one account
  // (their own queue), or else for a holder of the CSM team overview (the
  // whole team). Holding the queue alone with no accounts draws no pill.
  if (csmMode.value) keys.push('csm');
  if (csmEuMode.value) keys.push('csm_eu');
  return keys;
});
const badges = computed(() =>
  badgeKeys.value.map((key) => {
    const source = sourceOf(key);
    const count = source.count.value;
    const levels = source.levels.value;
    const where =
      key === 'finance'
        ? 'the finance queue'
        : isCsm(key)
          ? csmModeOf(key) === 'team'
            ? `the ${csmName(key)} team's accounts`
            : `your ${csmName(key)} queue`
          : 'your order queue';
    const label =
      key === 'finance'
        ? 'Needs Attention, finance queue'
        : isCsm(key)
          ? csmModeOf(key) === 'team'
            ? `Needs Attention, ${csmName(key)} team`
            : `Needs Attention, ${csmName(key)} queue`
          : 'Needs Attention';
    return {
      key,
      count,
      levels,
      levelsKnown: levels.delayed + levels.at_risk + levels.watch > 0,
      label,
      title:
        count === 0
          ? key === 'finance'
            ? 'Needs Attention — the finance queue is clear'
            : isCsm(key)
              ? csmModeOf(key) === 'team'
                ? `Needs Attention — nothing on the ${csmName(key)} team's accounts needs a look`
                : 'Needs Attention — nothing on your accounts needs a look'
              : 'Needs Attention — you are all caught up'
          : `Needs Attention — ${count} order${count === 1 ? '' : 's'} in ${where}: ` +
            `${levels.delayed} delayed, ${levels.at_risk} at risk, ${levels.watch} to keep an eye on`,
    };
  })
);
/*
 * AB#22137 — the phone's one queue button. Its number is what is delayed
 * across the queues (red), else how many are in them; an order in two queues
 * counts in each, as the pills it stands for do.
 */
const isPhone = usePhone();
const canSeeOwnMood = computed(() => can(OWN_PERMISSION));
const phoneDelayed = computed(() => badges.value.reduce((n, b) => n + (b.levels.delayed || 0), 0));
const phoneTotal = computed(() => badges.value.reduce((n, b) => n + (b.count || 0), 0));
const phoneName = (key: BadgeKey): string =>
  key === 'finance'
    ? 'Finance queue'
    : isCsm(key)
      ? csmModeOf(key) === 'team'
        ? `${csmName(key)} team`
        : `${csmName(key)} queue`
      : 'Order queue';
const phoneTone = computed(() =>
  phoneTotal.value === 0 ? 'calm' : phoneDelayed.value > 0 ? 'alert' : 'open'
);

const refreshCount = async (): Promise<void> => {
  // The CS count is read whenever the permission is held: it is what says
  // whether the person has accounts, and so whether the CS pill is drawn.
  const keys: BadgeKey[] = badgeKeys.value.filter((k) => !isCsm(k));
  const jobs = keys.map((key) => sourceOf(key).refreshCount());
  if (can('view-csm-needs-attention')) jobs.push(csmAttention.refreshCount());
  if (can('view-csm-eu-needs-attention')) jobs.push(csmEuAttention.refreshCount());
  await Promise.all(jobs);
  // The team's number only when the pill shows the team (no accounts of one's own).
  const teams: Promise<void>[] = [];
  if (csmMode.value === 'team') teams.push(csmTeamAttention.refreshCount());
  if (csmEuMode.value === 'team') teams.push(csmEuTeamAttention.refreshCount());
  await Promise.all(teams);
};
let attentionPoll: ReturnType<typeof setInterval> | null = null;

// Each pill opens its own queue; the page reads `queue` from the URL, so a
// press also switches the view when the page is already open.
const goToNeedsAttention = (key: BadgeKey): void => {
  router.push({
    name: 'needs-attention',
    query: {
      queue:
        key === 'finance'
          ? 'finance'
          : key === 'csm'
            ? csmMode.value === 'team'
              ? 'csm-team'
              : 'csm'
            : key === 'csm_eu'
              ? csmEuMode.value === 'team'
                ? 'csm-eu-team'
                : 'csm-eu'
              : 'order',
    },
  });
};

/**
 * Start counting once the person is known to hold the permission — and not
 * before. The count used to be fetched in onMounted behind a one-time
 * permission check, but the header mounts before the router guard has loaded
 * permissions, so that check was false at mount and the count (and the poll)
 * never started: the badge sat empty even with a full queue behind it. Watching
 * the permission starts the fetch the moment it resolves, however late, and the
 * guard stops it starting twice.
 */
const startAttention = (): void => {
  if (attentionPoll !== null) return;
  void refreshCount();
  attentionPoll = setInterval(() => void refreshCount(), 120000);
};

watch(canSeeNeedsAttention, (allowed) => allowed && startAttention(), { immediate: true });
// AB#22138 — learn whether this device has notifications on, once permissions are in.
watch(
  () => store.permissions?.length ?? 0,
  () => void push.load(),
  { immediate: true }
);

// Leaving the queue is the moment the count is most likely stale (the person
// just snoozed or dismissed things), so refresh the badge on the way out
// rather than waiting for the next poll tick.
watch(
  () => route.path,
  (to, from) => {
    if (from === '/needs-attention' && to !== '/needs-attention' && canSeeNeedsAttention.value) {
      void refreshCount();
    }
  }
);

onBeforeUnmount(() => {
  if (attentionPoll !== null) clearInterval(attentionPoll);
});
</script>
<style lang="scss">
/*
  AB#21246 — the menu toggle reads as a button.

  It was a bare icon with no target, no hover and no hint of what it did. Same
  circular target as the kebab at the other end of the bar, so the two ends of
  the header behave alike.
*/
.hdr-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  flex: 0 0 32px;
  border: 0;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.06);
  cursor: pointer;
  transition: background-color 0.12s ease;
}

/*
  AB#21594 — the Needs Attention entry point. A pill, not a bare icon: the
  count is the whole point, so it is set in a chip that is legible against the
  dark bar. Calm and translucent when the queue is clear; a red-ringed alert
  with the number when it is not.
*/
.na-entry {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 36px;
  padding: 0 6px 0 12px;
  border: 0;
  border-radius: 9999px;
  cursor: pointer;
  transition:
    background-color 0.12s ease,
    box-shadow 0.12s ease;
  outline: none;
}
.na-entry:focus-visible {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
}
.na-entry-ic {
  width: 20px;
  height: 20px;
  flex: 0 0 20px;
}
/* AB#22175 — "EU" beside the headset of the CSM EU pill. */
.na-entry-tag {
  margin-left: -4px;
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.04em;
  opacity: 0.85;
}
.na-entry-count {
  min-width: 24px;
  height: 24px;
  padding: 0 7px;
  border-radius: 9999px;
  font-size: 12.5px;
  font-weight: 800;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.na-entry-total {
  padding: 0 6px 0 2px;
  font-size: 12.5px;
  font-weight: 700;
  line-height: 1;
  color: #e6e9ee;
}
/* AB#21999 — two pills on a phone: keep the delayed number, drop the total. */
@media (max-width: 640px) {
  .na-entry--pair .na-entry-count + .na-entry-total {
    display: none;
  }
}
/*
  AB#22137 — phone only (below 768px). The menu toggle has nothing to do on a
  phone (the menu is the bottom bar), so the title gets its room; the queue
  pills fold behind .hdr-queues.
*/
@media (max-width: 767.98px) {
  .hdr-toggle {
    display: none;
  }
}
.hdr-queues {
  border: 0;
  padding: 0;
  background: transparent;
  border-radius: 9999px;
}
.hdr-queues-in {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 10px;
  border-radius: 9999px;
  color: #ffffff;
}
.hdr-queues-in--calm {
  background: rgba(255, 255, 255, 0.08);
}
.hdr-queues-in--open {
  background: rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}
.hdr-queues-in--open .na-entry-ic {
  color: #9db8ff;
}
.hdr-queues-in--alert {
  background: rgba(223, 4, 4, 0.16);
  box-shadow: inset 0 0 0 1px rgba(223, 4, 4, 0.4);
}
.hdr-queues-in--alert .na-entry-ic {
  color: #ff6b6b;
}
.hdr-queues-in--alert .na-entry-count {
  background: #df0404;
  color: #ffffff;
}
.hdr-sheet {
  display: flex;
  flex-direction: column;
  padding: 4px 8px 12px;
  min-width: 0;
}
.hdr-sheet-title {
  margin: 0;
  padding: 4px 12px 10px;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: var(--ink-mute);
}
.hdr-sheet-row,
.hdr-sheet-mood {
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 52px;
  padding: 0 12px;
  border: 0;
  border-radius: 12px;
  background: transparent;
  color: var(--ink);
  text-align: left;
  font-size: 15px;
  font-weight: 600;
}
.hdr-sheet-row:active {
  background: var(--surface-2);
}
.hdr-sheet-ic {
  width: 22px;
  height: 22px;
  flex: 0 0 22px;
  color: var(--ink-soft);
}
.hdr-sheet-name {
  flex: 1 1 auto;
  min-width: 0;
}
.hdr-sheet-clear {
  color: var(--success);
  font-size: 13px;
  font-weight: 700;
}
.hdr-sheet-delayed {
  padding: 2px 8px;
  border-radius: 999px;
  background: var(--danger);
  color: #ffffff;
  font-size: 12px;
  font-weight: 800;
}
.hdr-sheet-total {
  min-width: 28px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: var(--ink-soft);
}
/* The badge draws nothing while the mood feature is off: then neither does the row. */
.hdr-sheet-mood:not(:has(.cm-badge)) {
  display: none;
}
.hdr-sheet-mood {
  border-top: 1px solid var(--border);
  border-radius: 0;
  margin-top: 4px;
}

/* Something in the queue, nothing delayed: no red. */
.na-entry--open {
  background: rgba(255, 255, 255, 0.1);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.18);
}
.na-entry--open:hover {
  background: rgba(255, 255, 255, 0.18);
}
.na-entry--open .na-entry-ic {
  color: #9db8ff;
}
.na-entry-label {
  font-size: 12.5px;
  font-weight: 700;
  padding-right: 6px;
}

/* Clear: quiet, translucent, white shield. */
.na-entry--calm {
  background: rgba(255, 255, 255, 0.08);
  color: #cfd4da;
}
.na-entry--calm:hover {
  background: rgba(255, 255, 255, 0.16);
}
.na-entry--calm .na-entry-ic {
  color: #ffffff;
}

/* Alert: red-tinted pill, soft-red shield, solid red count chip. */
.na-entry--alert {
  background: rgba(223, 4, 4, 0.16);
  box-shadow: inset 0 0 0 1px rgba(223, 4, 4, 0.4);
}
.na-entry--alert:hover {
  background: rgba(223, 4, 4, 0.26);
}
.na-entry--alert .na-entry-ic {
  color: #ff6b6b;
}
.na-entry--alert .na-entry-count {
  background: #df0404;
  color: #ffffff;
}
.hdr-toggle:hover {
  background: rgba(255, 255, 255, 0.16);
}
.hdr-toggle:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}

/*
  AB#21597 — the account control. The company logo that used to sit here was the
  same image for everyone; the person's initials are identity, and the whole
  control (avatar + caret) is the button that opens Quick Access, so the separate
  kebab is gone. On the dark bar it reads as one obvious, clickable thing.
*/
.hdr-account {
  background: transparent;
  border: 0;
  padding: 3px;
  border-radius: 999px;
  transition: background-color 0.12s ease;
}
.hdr-account:hover {
  background: rgba(255, 255, 255, 0.06);
}
.hdr-account:focus-visible {
  outline: 2px solid #fff;
  outline-offset: 2px;
}
.hdr-avatar {
  width: 38px;
  height: 38px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.02em;
  color: #ffffff;
  /* AB#21597 — solid accent, matching the account avatar in the sidebar
     (.account__avatar), so the same person's initials read the same in both
     places rather than a gradient here and a flat circle there.
     AB#21839 — the disc itself is UserAvatar now (picture or initials). */
  background: var(--accent);
  flex-shrink: 0;
}
.hdr-avatar--sm {
  width: 22px;
  height: 22px;
  font-size: 10px;
}

/*
  AB#21594 — the Quick Access menu drops out of the dark header, so it is dark
  too: a translucent-panel look that belongs to the bar it hangs from, rather
  than the old white card that clashed with it.
*/
.dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  width: 224px;
  height: auto;
  border-radius: 12px;
  overflow: hidden;
  background: #1b1e23;
  border: 1px solid #2b3037;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.55);
  color: #e7ebef;

  .dropdown-header {
    background-color: #16181c !important;
    padding: 0.6rem 1.1rem;
    margin-bottom: 0;
    font-size: 0.7rem;
    font-weight: 800;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    color: #8b95a0;
  }

  .dropdown-list {
    .dropdown-item {
      color: #e7ebef;
      transition: background-color 0.12s ease;

      &:not(:last-of-type) {
        border-bottom: 1px solid #23272e;
      }

      &:hover {
        background-color: #23272e;
      }
    }

    .dropdown-item--toggle {
      cursor: pointer;
    }
  }
}

/* AB#22138 — what the notifications row says instead of a switch. */
.hdr-push-hint {
  font-size: 12px;
  font-weight: 700;
  color: #8b95a0;
}

/* AB#21595 — the small switch on the Dark mode row. */
.hdr-switch {
  width: 36px;
  height: 20px;
  border-radius: 9999px;
  background: #3a4048;
  position: relative;
  flex: 0 0 36px;
  transition: background-color 0.15s ease;
}
.hdr-switch.on {
  background: #009fe3;
}
.hdr-switch-dot {
  position: absolute;
  top: 2px;
  left: 2px;
  width: 16px;
  height: 16px;
  border-radius: 50%;
  background: #fff;
  transition: left 0.15s ease;
}
.hdr-switch.on .hdr-switch-dot {
  left: 18px;
}
</style>
